import { supabase } from '../lib/supabase';

// LinkedIn API publishing via Edge Function
export async function publishToLinkedIn(content, accessToken, contentPostId = null, images = []) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
    
    if (sessionError || !session?.access_token) {
      throw new Error('Session not found');
    }

    // Call Edge Function to publish
    const response = await fetch(
      `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/social-publisher`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          action: 'publish',
          provider: 'linkedin',
          content: content,
          userId: user?.id,
          contentPostId: contentPostId,
          images: images // Array of LinkedIn image URNs
        })
      }
    );

    const result = await response?.json();

    if (!result?.success) {
      if (result?.error === 'limit_reached' || response?.status === 402) {
        throw Object.assign(new Error(result?.message || 'Publish limit reached'), {
          type: 'LIMIT_REACHED',
          used: result?.used,
          limit: result?.limit,
          daysUntilReset: result?.daysUntilReset,
        });
      }
      const error = new Error(result?.error || 'Failed to publish to LinkedIn');
      if (result?.error?.includes('token expired')) {
        error.type = 'TOKEN_EXPIRED';
        error.provider = 'linkedin';
      } else if (result?.error?.includes('refresh failed')) {
        error.type = 'REFRESH_FAILED';
        error.provider = 'linkedin';
      }
      throw error;
    }

    return { success: true, postId: result?.postId };
  } catch (error) {
    console.error('LinkedIn publish error:', error);
    throw error;
  }
}

// Twitter API publishing via Edge Function
export async function publishToTwitter(content, accessToken, contentPostId = null, images = []) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
    
    if (sessionError || !session?.access_token) {
      throw new Error('Session not found');
    }

    // Call Edge Function to publish
    const response = await fetch(
      `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/social-publisher`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          action: 'publish',
          provider: 'twitter',
          content: content,
          userId: user?.id,
          contentPostId: contentPostId,
          images: images // Array of Twitter media IDs
        })
      }
    );

    const result = await response?.json();

    if (!result?.success) {
      if (result?.error === 'limit_reached' || response?.status === 402) {
        throw Object.assign(new Error(result?.message || 'Publish limit reached'), {
          type: 'LIMIT_REACHED',
          used: result?.used,
          limit: result?.limit,
          daysUntilReset: result?.daysUntilReset,
        });
      }
      const error = new Error(result?.error || 'Failed to publish to Twitter');
      if (result?.error?.includes('token expired')) {
        error.type = 'TOKEN_EXPIRED';
        error.provider = 'twitter';
      } else if (result?.error?.includes('refresh failed')) {
        error.type = 'REFRESH_FAILED';
        error.provider = 'twitter';
      }
      throw error;
    }

    return { success: true, postId: result?.postId };
  } catch (error) {
    console.error('Twitter publish error:', error);
    throw error;
  }
}

// Validate token before publishing via Edge Function
export async function validateToken(provider) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      return { valid: false, error: 'User not authenticated' };
    }

    const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
    
    if (sessionError || !session?.access_token) {
      return { valid: false, error: 'Session not found' };
    }

    // Call Edge Function to validate token
    const response = await fetch(
      `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/social-publisher`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          action: 'validate',
          provider: provider,
          userId: user?.id
        })
      }
    );

    const result = await response?.json();

    if (!result?.success) {
      return { valid: false, error: result?.error || 'Validation failed' };
    }

    return { valid: result?.valid, error: result?.error };
  } catch (error) {
    console.error('Token validation error:', error);
    return { valid: false, error: error?.message };
  }
}

// Get user's OAuth tokens from profile
export async function getUserTokens() {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    const { data: profile, error: profileError } = await supabase
      ?.from('user_profiles')
      ?.select('linkedin_access_token, twitter_access_token, linkedin_token_expires_at, twitter_token_expires_at')
      ?.eq('id', user?.id)
      ?.maybeSingle();

    if (profileError) {
      throw profileError;
    }

    return {
      linkedin: {
        token: profile?.linkedin_access_token,
        expiry: profile?.linkedin_token_expires_at
      },
      twitter: {
        token: profile?.twitter_access_token,
        expiry: profile?.twitter_token_expires_at
      }
    };
  } catch (error) {
    console.error('Error fetching user tokens:', error);
    throw error;
  }
}